/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next'
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil'
import { TFileInfo } from '../types/appTypes'
import { Spin, message } from 'antd'
import Dropzone from './Dropzone'
import DocumentItem from './DocumentItem'
import NoData from './NoData'
import {
  currentAccountState,
  currentWorkspaceState,
  documentListState,
  documentState,
  pageListState,
  requestState,
} from '../recoil/atoms'
import { IDocument, RegisterDocumentUseCase, UploadDocumentUseCase } from '@axelity/actasign-sic'
import { useEffect, useState } from 'react'
import SectionTitle from '../elements/SectionTitle'
import { filterValidPdfs } from '../utils/pdfValidation'

type TDocumentListProps = {
  canAdd?: boolean
  canView?: boolean
  canPrepare?: boolean
  canDownload?: boolean
  canRemove?: boolean
  showStatus?: boolean
}

const CONTENT_TYPE = 'application/pdf'

const DocumentList = ({
  canAdd = false,
  canRemove = false,
  canDownload = false,
  canPrepare = false,
  canView = false,
  showStatus = true,
}: TDocumentListProps) => {
  const { t } = useTranslation(['documentList'])

  const account = useRecoilValue(currentAccountState)
  const workspace = useRecoilValue(currentWorkspaceState)
  const request = useRecoilValue(requestState)
  const resetDocument = useResetRecoilState(documentState)
  const resetPages = useResetRecoilState(pageListState)

  const [documentList, setDocumentList] = useRecoilState(documentListState)

  const [inProgress, setInProgress] = useState<boolean>(false)

  const handleSelectedFiles = async (files: TFileInfo[]) => {
    setInProgress(true)

    try {
      // Validate all PDFs before uploading
      message.loading({ content: t('pdfVerification.checking'), key: 'pdfValidation' })
      const { validFiles, invalidFiles, results } = await filterValidPdfs(files)

      // Show errors for invalid files
      invalidFiles.forEach((invalidFile) => {
        const result = results.find((r) => r.file.filename === invalidFile.filename)
        if (result && result.errorKey) {
          message.error({
            content: t(result.errorKey, { filename: invalidFile.filename }),
            duration: 8,
          })
          console.error('PDF validation failed:', {
            filename: invalidFile.filename,
            reason: result.errorKey,
            details: result.errorDetails,
          })
        }
      })

      message.destroy('pdfValidation')

      // Only upload valid files
      if (validFiles.length === 0) {
        message.warning(t('pdfVerification.failed'))
        setInProgress(false)
        return
      }

      // Upload valid files
      let docs: IDocument[] = [...documentList]
      for (let i = 0; i < validFiles.length; i++) {
        try {
          console.log(`[DocumentList] Registering document: ${validFiles[i].filename}`)
          const _document = await new RegisterDocumentUseCase().register({
            account: account._id,
            workspace: workspace._id,
            process: request._id,
            contentType: CONTENT_TYPE,
            filename: validFiles[i].filename,
            toBeSigned: true,
            displayName: validFiles[i].filename.toLowerCase().endsWith('.pdf')
              ? validFiles[i].filename.substring(0, validFiles[i].filename.length - 4)
              : validFiles[i].filename,
          })
          console.log(`[DocumentList] Document registered with ID: ${_document._id}`)

          // Check if content is valid
          console.log(`[DocumentList] validFiles[${i}].content type:`, validFiles[i].content?.constructor?.name)
          console.log(`[DocumentList] validFiles[${i}].content instanceof ArrayBuffer:`, validFiles[i].content instanceof ArrayBuffer)
          console.log(`[DocumentList] validFiles[${i}].content length:`, (validFiles[i].content as ArrayBuffer)?.byteLength)

          const content = new Blob([validFiles[i].content as ArrayBuffer], { type: CONTENT_TYPE })
          console.log(`[DocumentList] Blob created - size: ${content.size} bytes, type: ${content.type}`)

          if (content.size === 0) {
            console.error(`[DocumentList] ❌ ERROR: Blob size is 0! Content was not properly transferred.`)
            throw new Error('File content is empty')
          }

          await new UploadDocumentUseCase({
            documentId: _document._id,
          }).upload({
            content,
          })

          console.log(`[DocumentList] ✅ Upload completed successfully for: ${validFiles[i].filename} (${content.size} bytes)`)

          docs = [...docs, _document]
        } catch (error) {
          console.error(`[DocumentList] ❌ Error uploading ${validFiles[i].filename}:`, error)
          message.error(t('pdfVerification.failed'))
        }
      }

      setDocumentList([...docs])

      // Show summary if some files were rejected
      if (invalidFiles.length > 0) {
        message.info(
          t('common:info', {
            defaultValue: `${validFiles.length} of ${files.length} files uploaded successfully`,
          }),
          5
        )
      }
    } catch (error) {
      console.error('Document upload error:', error)
      message.error(t('pdfVerification.failed'))
    }

    setInProgress(false)
  }

  useEffect(() => {
    resetDocument()
    resetPages()
  }, [])

  return (
    <div className="w-full">
      <Spin tip={t('documentList:action.loading')} spinning={inProgress} size="large">
        <SectionTitle title={t('title')} />

        {canAdd ? (
          <Dropzone
            mimeType={CONTENT_TYPE}
            multiple={true}
            onSelect={handleSelectedFiles}
            upLoadInstructions={t('uploadInstructions')}
          />
        ) : null}

        {documentList.length !== 0 ? (
          <div className="flex flex-wrap justify-evenly max-w-4xl mx-auto text-base p-2 mt-4 rounded-md">
            {documentList.map((item) => {
              return (
                <DocumentItem
                  key={item._id}
                  canPrepare={canPrepare}
                  canDownload={canDownload}
                  canRemove={canRemove}
                  canView={canView}
                  content={item}
                  showStatus={showStatus}
                />
              )
            })}
          </div>
        ) : (
          <NoData note={t('noDocuments')} />
        )}
      </Spin>
    </div>
  )
}

export default DocumentList
